import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Award, FileText, Clock, ChevronRight, ChevronLeft,
  CheckCircle, XCircle, Search, Filter, Download, Eye, Trash2,
  Plus, Settings, BarChart3, AlertTriangle, RefreshCw, LogOut,
  BookOpen, User, Mail, Calendar, Activity, Tag, Upload, FileCheck, Image, Lock, Wrench
} from 'lucide-react';
import Badge from '@/components/Badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPortalPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('students');
  const [loading, setLoading] = useState(true);
  
  // Course filter
  const [adminCourse, setAdminCourse] = useState('all');
  
  // Data states
  const [students, setStudents] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [examAttempts, setExamAttempts] = useState([]);
  const [proctorEmails, setProctorEmails] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Selected student for detail view
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentProgress, setStudentProgress] = useState(null);
  const [studentDocuments, setStudentDocuments] = useState([]);
  const [uploadingBgCheck, setUploadingBgCheck] = useState(false);
  const bgCheckFileRef = useRef(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [auditFilter, setAuditFilter] = useState('');
  
  // New discount code form
  const [newDiscountCode, setNewDiscountCode] = useState({ code: '', discount_percent: 100, max_uses: 1, quantity: 1, send_to_email: '', course: '' });
  const [showDiscountForm, setShowDiscountForm] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    loadAllData();
  }, [isAdmin]);

  // Reload when course filter changes
  useEffect(() => {
    if (isAdmin) loadAllData();
  }, [adminCourse]);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const courseParam = adminCourse !== 'all' ? `?course=${adminCourse}` : '';
      const [studentsRes, certsRes, logsRes, codesRes, statsRes, examsRes, proctorEmailsRes] = await Promise.all([
        axios.get(`${API}/admin/students${courseParam}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/certificates/pending${courseParam}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/audit-logs?limit=200`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/discount-codes`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/exam-attempts${courseParam}`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/proctor-emails`, { headers: getAuthHeaders() }).catch(() => ({ data: [] }))
      ]);
      
      setStudents(studentsRes.data);
      setCertificates(certsRes.data);
      setAuditLogs(logsRes.data);
      setDiscountCodes(codesRes.data);
      setStats(statsRes.data);
      setExamAttempts(examsRes.data || []);
      setProctorEmails(proctorEmailsRes.data || []);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const loadStudentProgress = async (studentId) => {
    try {
      const courseParam = adminCourse !== 'all' ? `?course=${adminCourse}` : '';
      const response = await axios.get(`${API}/admin/students/${studentId}/progress${courseParam}`, { headers: getAuthHeaders() });
      setStudentProgress(response.data);
      setSelectedStudent(students.find(s => s.id === studentId));
      // Also load student documents
      loadStudentDocuments(studentId);
    } catch (error) {
      toast.error('Failed to load student progress');
    }
  };

  const loadStudentDocuments = async (studentId) => {
    try {
      const response = await axios.get(`${API}/admin/students/${studentId}/documents`, { headers: getAuthHeaders() });
      setStudentDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setStudentDocuments([]);
    }
  };

  const handleUploadBackgroundCheck = async (studentId, file) => {
    if (!file) return;
    
    setUploadingBgCheck(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(
        `${API}/admin/students/${studentId}/documents/background-check`,
        formData,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data' 
          } 
        }
      );
      
      toast.success('Background check uploaded successfully');
      loadStudentDocuments(studentId);
    } catch (error) {
      toast.error('Failed to upload background check');
    } finally {
      setUploadingBgCheck(false);
      if (bgCheckFileRef.current) {
        bgCheckFileRef.current.value = '';
      }
    }
  };

  const viewDocument = async (studentId, docType) => {
    try {
      // For ID documents, we need to get the data from the user record
      const courseParam = adminCourse !== 'all' ? `?course=${adminCourse}` : '';
      const response = await axios.get(`${API}/admin/students/${studentId}/progress${courseParam}`, { headers: getAuthHeaders() });
      const student = response.data.student;
      
      let docData = null;
      if (docType === 'id' && student.id_document) {
        docData = student.id_document;
      } else if (docType === 'background_check' && student.background_check) {
        docData = student.background_check;
      }
      
      if (docData && docData.data) {
        // Create a blob from base64 data and open in new tab
        const byteCharacters = atob(docData.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: docData.content_type });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        toast.error('Document not found');
      }
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  const handleCertificateReview = async (certId, approve) => {
    try {
      await axios.post(`${API}/admin/certificates/${certId}/review`, 
        { approve, essay_score: approve ? 100 : 0 },
        { headers: getAuthHeaders() }
      );
      toast.success(approve ? 'Certificate approved!' : 'Certificate rejected');
      loadAllData();
    } catch (error) {
      toast.error('Failed to review certificate');
    }
  };

  const handleCreateDiscountCode = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/discount-codes`, {
        code: newDiscountCode.code || null,
        discount_percent: parseInt(newDiscountCode.discount_percent),
        max_uses: parseInt(newDiscountCode.max_uses) || 1,
        quantity: parseInt(newDiscountCode.quantity) || 1,
        send_to_email: newDiscountCode.send_to_email || null,
        course: newDiscountCode.course || null
      }, { headers: getAuthHeaders() });
      const codes = res.data.codes || [];
      const emailStatus = res.data.email_sent;
      const emailError = res.data.email_error;
      
      if (emailStatus === 'success') {
        toast.success(`Created ${codes.length} code(s): ${codes.join(', ')} — emailed to ${newDiscountCode.send_to_email}`);
      } else if (emailStatus === 'error') {
        toast.success(`Created ${codes.length} code(s): ${codes.join(', ')}`);
        toast.error(`Email failed to send: ${emailError || 'Unknown error'}. The code was still created — you can share it manually.`);
      } else if (newDiscountCode.send_to_email) {
        toast.success(`Created ${codes.length} code(s): ${codes.join(', ')}`);
        toast.warning('Email was not sent. The code was created — share it manually.');
      } else {
        toast.success(`Created ${codes.length} code(s): ${codes.join(', ')}`);
      }
      setNewDiscountCode({ code: '', discount_percent: 100, max_uses: 1, quantity: 1, send_to_email: '', course: '' });
      setShowDiscountForm(false);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create discount code');
    }
  };

  const handleDeleteDiscountCode = async (code) => {
    if (!window.confirm(`Delete discount code "${code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${code}`, { headers: getAuthHeaders() });
      toast.success('Discount code deleted');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete discount code');
    }
  };

  const handleResetPassword = async (studentId, studentEmail) => {
    const newPassword = window.prompt(`Enter new password for ${studentEmail}:\n(minimum 6 characters)`);
    if (!newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.post(`${API}/admin/students/${studentId}/reset-password`, 
        { new_password: newPassword },
        { headers: getAuthHeaders() }
      );
      toast.success(`Password reset for ${studentEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDeleteStudent = async (studentId, studentEmail) => {
    if (!window.confirm(`Delete student "${studentEmail}" and all their data? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/students/${studentId}`, { headers: getAuthHeaders() });
      toast.success('Student deleted');
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete student');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const filteredStudents = students.filter(s => 
    s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredLogs = auditLogs.filter(log =>
    auditFilter === '' || log.action?.includes(auditFilter)
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading Admin Portal...</div>
      </div>
    );
  }

  // Student Detail View
  if (selectedStudent && studentProgress) {
    const s = studentProgress.student;
    const certs = studentProgress.certificates || [];
    const photo = studentProgress.photo;
    const completedChapters = studentProgress.chapter_progress.filter(p => p.status === 'completed').length;
    const studentCourse = s.enrolled_course || (s.paid_courses && s.paid_courses.length > 0 ? s.paid_courses[s.paid_courses.length - 1] : 'bail-enforcement');
    const totalChapters = studentCourse === 'pre-licensing' ? 7 : 10;
    const tt = studentProgress.time_tracking || {};
    const totalHours = tt.total_hours || 0;
    const totalMins = tt.total_minutes || 0;
    const hours = Math.floor(totalHours);
    const mins = totalMins - (hours * 60);

    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Button variant="ghost" onClick={() => { setSelectedStudent(null); setStudentProgress(null); }}>
              <ChevronLeft className="w-5 h-5 mr-2" /> Back to Students
            </Button>
            <h1 className="font-bold text-lg" style={{ fontFamily: 'Barlow Condensed' }}>STUDENT FILE</h1>
            <div />
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
          {/* Top: Photo + ID Card + Contact Info */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Student Profile Card */}
            <div className="bg-card border border-border rounded-sm p-6 flex flex-col items-center text-center">
              {photo ? (
                <img src={photo} alt="Student" className="w-28 h-28 rounded-full object-cover border-4 border-primary/20 mb-4" />
              ) : (
                <div className="w-28 h-28 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <User className="w-14 h-14 text-primary/40" />
                </div>
              )}
              <h2 className="text-2xl font-black" style={{ fontFamily: 'Barlow Condensed' }}>
                {s.first_name} {s.last_name}
              </h2>
              <span className={`mt-2 px-3 py-1 rounded-sm text-xs font-bold ${s.has_paid ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-500'}`}>
                {s.has_paid ? 'ENROLLED' : 'NOT ENROLLED'}
              </span>
              {certs.length > 0 && certs.some(c => c.status === 'approved') && (
                <span className="mt-2 px-3 py-1 bg-primary/10 text-primary rounded-sm text-xs font-bold">CERTIFIED</span>
              )}
            </div>

            {/* Contact Information */}
            <div className="bg-card border border-border rounded-sm p-6">
              <h3 className="font-bold text-sm text-muted-foreground uppercase tracking-wider mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span>{s.email}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Activity className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span>{s.phone || 'No phone on file'}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span>Registered: {formatDate(s.created_at)}</span>
                </div>
                {s.last_login && (
                  <div className="flex items-center gap-3 text-sm">
                    <Activity className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span>Last Login: {formatDate(s.last_login)}</span>
                  </div>
                )}
                {s.enrolled_course && (
                  <div className="flex items-center gap-3 text-sm">
                    <BookOpen className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="font-semibold">
                      {s.enrolled_course === 'pre-licensing' ? 'Bail Bond Pre-Licensing' : 'Bail Enforcement Certification'}
                    </span>
                  </div>
                )}
                {s.discount_code_used && (
                  <div className="flex items-center gap-3 text-sm">
                    <Tag className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span>Discount: <span className="font-mono text-xs">{s.discount_code_used}</span></span>
                  </div>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-border flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => handleResetPassword(selectedStudent.id, s.email)} data-testid="reset-password-btn">
                  <Lock className="w-4 h-4 mr-1" /> Reset Password
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="reset-course-progress-btn"
                  onClick={async () => {
                    const courseName = s.enrolled_course || 'pre-licensing';
                    const courseLabel = courseName === 'pre-licensing' ? 'Bail Bond Pre-Licensing' : 'Bail Enforcement Certification';
                    if (!window.confirm(`Reset ALL ${courseLabel} progress for ${s.first_name} ${s.last_name}?\n\nThis will erase:\n- All chapter completion\n- All quiz attempts\n- All page view history\n- All exam attempts\n\nThis cannot be undone.`)) return;
                    try {
                      const res = await axios.delete(`${API}/admin/students/${selectedStudent.id}/course-progress/${courseName}`, { headers: getAuthHeaders() });
                      toast.success(`${courseLabel} progress reset: ${res.data.chapters_deleted} chapters, ${res.data.quizzes_deleted} quizzes cleared`);
                      loadStudentProgress(selectedStudent.id);
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset progress'); }
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-1" /> Reset Course Progress
                </Button>
                <Button
                  size="sm"
                  variant={s.is_suspended ? "outline" : "destructive"}
                  data-testid="suspend-btn"
                  onClick={async () => {
                    if (s.is_suspended) {
                      if (!window.confirm(`Reinstate ${s.first_name} ${s.last_name}'s account?`)) return;
                      try {
                        await axios.post(`${API}/admin/students/${selectedStudent.id}/suspend`, { suspend: false }, { headers: getAuthHeaders() });
                        toast.success('Account reinstated');
                        loadStudentProgress(selectedStudent.id);
                      } catch { toast.error('Failed to reinstate'); }
                    } else {
                      const reason = prompt(`Why are you suspending ${s.first_name} ${s.last_name}'s account?`);
                      if (reason === null) return;
                      try {
                        await axios.post(`${API}/admin/students/${selectedStudent.id}/suspend`, { suspend: true, reason }, { headers: getAuthHeaders() });
                        toast.success('Account suspended');
                        loadStudentProgress(selectedStudent.id);
                      } catch { toast.error('Failed to suspend'); }
                    }
                  }}
                >
                  {s.is_suspended ? 'Reinstate Account' : 'Suspend Account'}
                </Button>
              </div>
              {s.is_suspended && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
                  <p className="text-sm font-bold text-red-600">ACCOUNT SUSPENDED</p>
                  {s.suspended_reason && <p className="text-xs text-red-500 mt-1">{s.suspended_reason}</p>}
                </div>
              )}
            </div>

            {/* Certificates & ID */}
            <div className="bg-card border border-border rounded-sm p-6">
              <h3 className="font-bold text-sm text-muted-foreground uppercase tracking-wider mb-4">Certificates & ID</h3>
              {certs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No certificates issued yet</p>
              ) : (
                <div className="space-y-3">
                  {certs.map((cert, i) => (
                    <div key={i} className="border border-border rounded-sm p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-bold text-sm">{cert.certificate_number}</span>
                        <span className={`px-2 py-0.5 rounded-sm text-xs font-bold ${
                          cert.status === 'approved' ? 'bg-green-500/10 text-green-600' : 'bg-yellow-500/10 text-yellow-600'
                        }`}>
                          {(cert.status || '').toUpperCase().replace('_', ' ')}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {cert.course === 'pre-licensing' ? 'Bail Bond Pre-Licensing' : 'Bail Enforcement Certification'}
                      </p>
                      {cert.final_exam_score && (
                        <p className="text-xs text-muted-foreground">Exam Score: {cert.final_exam_score}%</p>
                      )}
                      {cert.course_completed && (
                        <p className="text-xs text-muted-foreground">Completed: {formatDate(cert.course_completed)}</p>
                      )}
                      {cert.status === 'pending_review' && (
                        <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white flex-1"
                            data-testid={`approve-cert-${cert.id}`}
                            onClick={async () => {
                              if (!window.confirm(`Approve certificate ${cert.certificate_number}?`)) return;
                              try {
                                await axios.post(`${API}/admin/certificates/${cert.id}/review`, { approve: true, essay_score: 100 }, { headers: getAuthHeaders() });
                                toast.success(`Certificate ${cert.certificate_number} approved`);
                                loadStudentProgress(selectedStudent.id);
                                loadAllData();
                              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to approve'); }
                            }}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50 flex-1"
                            data-testid={`reject-cert-${cert.id}`}
                            onClick={async () => {
                              const reason = window.prompt('Rejection reason (optional):');
                              if (reason === null) return;
                              try {
                                await axios.post(`${API}/admin/certificates/${cert.id}/review`, { approve: false, rejection_reason: reason }, { headers: getAuthHeaders() });
                                toast.success(`Certificate ${cert.certificate_number} rejected`);
                                loadStudentProgress(selectedStudent.id);
                                loadAllData();
                              } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject'); }
                            }}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Course Progress Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-sm p-4 text-center">
              <p className="text-3xl font-black" style={{ fontFamily: 'Barlow Condensed' }}>{completedChapters}/{totalChapters}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Chapters Completed</p>
            </div>
            <div className="bg-card border border-border rounded-sm p-4 text-center">
              <p className="text-3xl font-black" style={{ fontFamily: 'Barlow Condensed' }}>{hours}h {mins}m</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Total Study Time</p>
            </div>
            <div className="bg-card border border-border rounded-sm p-4 text-center">
              <p className="text-3xl font-black" style={{ fontFamily: 'Barlow Condensed' }}>
                {studentProgress.quiz_attempts.filter(q => q.passed).length}/{studentProgress.quiz_attempts.length}
              </p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mt-1">Quizzes Passed</p>
            </div>
          </div>

          {/* Chapter Progress + Quiz Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Chapter Progress */}
            <div className="bg-card border border-border rounded-sm p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-wider">
                <BookOpen className="w-4 h-4" /> Chapter Progress
              </h3>
              <div className="space-y-2">
                {studentProgress.chapter_progress.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No progress yet</p>
                ) : (
                  studentProgress.chapter_progress.map((cp) => (
                    <div key={cp.chapter_number || cp.chapter_id} className="flex items-center justify-between text-sm py-2 border-b border-border last:border-0">
                      <div>
                        <span className="font-semibold">Ch. {cp.chapter_number}</span>
                        {cp.chapter_title && <span className="text-muted-foreground ml-2 text-xs">{cp.chapter_title}</span>}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-muted-foreground text-xs">{Math.floor((cp.time_spent_seconds || 0) / 60)}m</span>
                        <span className={`px-2 py-0.5 rounded-sm text-xs font-bold ${
                          cp.status === 'completed' ? 'bg-green-500/10 text-green-600' : 'bg-primary/10 text-primary'
                        }`}>
                          {cp.status === 'completed' ? 'DONE' : 'IN PROGRESS'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Quiz Attempts */}
            <div className="bg-card border border-border rounded-sm p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-wider">
                <FileText className="w-4 h-4" /> Quiz & Exam Results
              </h3>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {studentProgress.quiz_attempts.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No attempts yet</p>
                ) : (
                  studentProgress.quiz_attempts.map((qa, i) => (
                    <div key={i} className="flex items-center justify-between text-sm py-2 border-b border-border last:border-0">
                      <span className="font-semibold">{qa.is_final_exam ? 'Final Exam' : 'Chapter Quiz'}</span>
                      <div className="flex items-center gap-3">
                        <span className={`font-bold ${qa.passed ? 'text-green-600' : 'text-red-500'}`}>{qa.percentage}%</span>
                        <span className={`px-2 py-0.5 rounded-sm text-xs font-bold ${
                          qa.passed ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-500'
                        }`}>
                          {qa.passed ? 'PASSED' : 'FAILED'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="bg-card border border-border rounded-sm p-6">
            <h3 className="font-bold mb-4 flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-wider">
              <FileCheck className="w-4 h-4" /> Student Documents
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* ID Document */}
              <div className="border border-border rounded-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Image className="w-5 h-5 text-muted-foreground" />
                    <span className="font-medium">Government ID</span>
                  </div>
                  {studentDocuments.find(d => d.type === 'id') ? (
                    <span className="text-xs bg-green-500/10 text-green-500 px-2 py-0.5 rounded-sm">UPLOADED</span>
                  ) : (
                    <span className="text-xs bg-yellow-500/10 text-yellow-500 px-2 py-0.5 rounded-sm">MISSING</span>
                  )}
                </div>
                {studentDocuments.find(d => d.type === 'id') ? (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {studentDocuments.find(d => d.type === 'id')?.filename}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Uploaded: {formatDate(studentDocuments.find(d => d.type === 'id')?.uploaded_at)}
                    </p>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => viewDocument(selectedStudent.id, 'id')}
                      className="w-full"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View ID
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Student has not uploaded ID</p>
                )}
              </div>

              {/* Background Check */}
              <div className="border border-border rounded-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-muted-foreground" />
                    <span className="font-medium">Background Check</span>
                  </div>
                  {studentDocuments.find(d => d.type === 'background_check') ? (
                    <span className="text-xs bg-green-500/10 text-green-500 px-2 py-0.5 rounded-sm">UPLOADED</span>
                  ) : (
                    <span className="text-xs bg-yellow-500/10 text-yellow-500 px-2 py-0.5 rounded-sm">PENDING</span>
                  )}
                </div>
                {studentDocuments.find(d => d.type === 'background_check') ? (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {studentDocuments.find(d => d.type === 'background_check')?.filename}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Uploaded: {formatDate(studentDocuments.find(d => d.type === 'background_check')?.uploaded_at)}
                    </p>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => viewDocument(selectedStudent.id, 'background_check')}
                      className="w-full"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Background Check
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground mb-2">No background check uploaded yet</p>
                    <input
                      ref={bgCheckFileRef}
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      onChange={(e) => handleUploadBackgroundCheck(selectedStudent.id, e.target.files[0])}
                    />
                    <Button 
                      size="sm" 
                      onClick={() => bgCheckFileRef.current?.click()}
                      disabled={uploadingBgCheck}
                      className="w-full"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadingBgCheck ? 'Uploading...' : 'Upload Background Check'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Certificate */}
              <div className="border border-border rounded-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-muted-foreground" />
                    <span className="font-medium">Certificate</span>
                  </div>
                  {studentDocuments.find(d => d.type === 'certificate' && d.status === 'approved') ? (
                    <span className="text-xs bg-green-500/10 text-green-500 px-2 py-0.5 rounded-sm">ISSUED</span>
                  ) : studentDocuments.find(d => d.type === 'certificate') ? (
                    <span className="text-xs bg-yellow-500/10 text-yellow-500 px-2 py-0.5 rounded-sm">
                      {studentDocuments.find(d => d.type === 'certificate')?.status?.toUpperCase()}
                    </span>
                  ) : (
                    <span className="text-xs bg-secondary text-muted-foreground px-2 py-0.5 rounded-sm">NOT EARNED</span>
                  )}
                </div>
                {studentDocuments.find(d => d.type === 'certificate') ? (
                  <div className="space-y-2">
                    <p className="text-sm font-mono">
                      #{studentDocuments.find(d => d.type === 'certificate')?.certificate_number}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Issued: {formatDate(studentDocuments.find(d => d.type === 'certificate')?.issued_at)}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Certificate will be issued upon course completion</p>
                )}
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-sm">
              <p className="text-xs text-muted-foreground">
                <strong>Colorado Compliance:</strong> All student records must be retained for 5 years per DOI requirements. 
                Documents are securely stored and available for audit purposes.
              </p>
            </div>
          </div>

          {/* Hours & Activity — DOI Audit Trail */}
          {tt.activity_log && tt.activity_log.length > 0 && (
            <div className="bg-card border border-border rounded-sm p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-wider">
                <Clock className="w-4 h-4" /> Hours & Activity (Mountain Time)
              </h3>
              
              {/* Daily Summary */}
              {tt.daily_summary && tt.daily_summary.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-semibold">Daily Study Log</h4>
                  <div className="border border-border rounded-sm overflow-hidden">
                    <div className="grid grid-cols-3 gap-4 p-3 bg-secondary/50 text-xs font-semibold text-muted-foreground">
                      <div>Date</div>
                      <div>Hours</div>
                      <div>Minutes</div>
                    </div>
                    {tt.daily_summary.map((day, i) => (
                      <div key={i} className="grid grid-cols-3 gap-4 p-3 text-sm border-t border-border">
                        <div className="font-medium">{day.date}</div>
                        <div>{day.hours}h</div>
                        <div>{day.minutes}m</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Chapter Summary */}
              {tt.chapter_summary && tt.chapter_summary.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-semibold">Per-Chapter Hours</h4>
                  <div className="border border-border rounded-sm overflow-hidden">
                    <div className="grid grid-cols-4 gap-4 p-3 bg-secondary/50 text-xs font-semibold text-muted-foreground">
                      <div>Course</div>
                      <div>Chapter</div>
                      <div>Hours</div>
                      <div>Minutes</div>
                    </div>
                    {tt.chapter_summary.map((ch, i) => (
                      <div key={i} className="grid grid-cols-4 gap-4 p-3 text-sm border-t border-border">
                        <div className="text-xs">{ch.course === 'pre-licensing' ? 'Pre-Licensing' : 'Enforcement'}</div>
                        <div className="font-medium">Ch. {ch.chapter_number}</div>
                        <div>{ch.hours}h</div>
                        <div>{ch.minutes}m</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Detailed Page-by-Page Activity */}
              <div>
                <h4 className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-semibold">
                  Detailed Page Activity ({tt.activity_log.length} records)
                </h4>
                <div className="border border-border rounded-sm overflow-hidden max-h-64 overflow-y-auto">
                  <div className="grid grid-cols-6 gap-2 p-3 bg-secondary/50 text-xs font-semibold text-muted-foreground sticky top-0">
                    <div>Date</div>
                    <div>Time (MT)</div>
                    <div>Chapter</div>
                    <div>Page</div>
                    <div>Required</div>
                    <div>Actual</div>
                  </div>
                  {tt.activity_log.map((entry, i) => (
                    <div key={i} className="grid grid-cols-6 gap-2 p-3 text-xs border-t border-border">
                      <div>{entry.date_mt}</div>
                      <div>{entry.time_mt}</div>
                      <div>Ch. {entry.chapter_number}</div>
                      <div>P{entry.page_number}</div>
                      <div>{Math.floor(entry.min_read_seconds / 60)}m{entry.min_read_seconds % 60}s</div>
                      <div className={entry.met_minimum ? 'text-green-600' : 'text-red-500'}>
                        {Math.floor(entry.actual_seconds / 60)}m{entry.actual_seconds % 60}s
                        {entry.met_minimum ? ' ✓' : ' ✗'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Audit Logs */}
          <div className="bg-card border border-border rounded-sm p-6">
            <h3 className="font-bold mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Activity Log
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {studentProgress.audit_logs.slice(0, 20).map((log, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-2 border-b border-border">
                  <span className="font-mono text-xs bg-secondary px-2 py-0.5 rounded">{log.action}</span>
                  <span className="text-muted-foreground text-xs">{log.timestamp_mt || formatDate(log.timestamp)}</span>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Main Admin Portal
  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 bg-card border-r border-border h-screen fixed left-0 top-0 flex flex-col">
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Badge className="w-8 h-8" />
            <div>
              <span className="font-bold tracking-tight block" style={{ fontFamily: 'Barlow Condensed' }}>
                ADMIN PORTAL
              </span>
              <span className="text-xs text-muted-foreground">Course Management</span>
            </div>
          </div>
          <select
            data-testid="admin-course-filter"
            value={adminCourse}
            onChange={(e) => { setAdminCourse(e.target.value); setSelectedStudent(null); }}
            className="mt-3 w-full px-3 py-2 rounded-sm border border-border bg-background text-sm font-semibold"
          >
            <option value="all">All Courses</option>
            <option value="bail-enforcement">Bail Enforcement Certification</option>
            <option value="pre-licensing">Bail Bond Pre-Licensing</option>
          </select>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          <button
            onClick={() => setActiveTab('students')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'students' ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'
            }`}
          >
            <Users className="w-5 h-5" />
            Students
          </button>
          <button
            onClick={() => setActiveTab('certificates')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'certificates' ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'
            }`}
          >
            <Award className="w-5 h-5" />
            Certificates
            {certificates.length > 0 && (
              <span className="ml-auto bg-accent text-accent-foreground text-xs px-2 py-0.5 rounded-full">
                {certificates.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('exams')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'exams' ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'
            }`}
          >
            <FileText className="w-5 h-5" />
            Exam Results
          </button>
          <button
            onClick={() => setActiveTab('discounts')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'discounts' ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'
            }`}
          >
            <Tag className="w-5 h-5" />
            Discount Codes
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'audit' ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'
            }`}
          >
            <Activity className="w-5 h-5" />
            Audit Logs
          </button>
          <button
            onClick={() => setActiveTab('proctor-emails')}
            data-testid="proctor-emails-tab"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'proctor-emails' ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'
            }`}
          >
            <Mail className="w-5 h-5" />
            Proctor Emails
            {proctorEmails.length > 0 && (
              <span className="ml-auto bg-secondary text-secondary-foreground text-xs px-2 py-0.5 rounded-full">
                {proctorEmails.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('tools')}
            data-testid="tools-tab"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
              activeTab === 'tools' ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'
            }`}
          >
            <Wrench className="w-5 h-5" />
            Data Tools
          </button>
        </nav>

        <div className="p-4 border-t border-border space-y-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-start" data-testid="admin-course-view-trigger">
                <BookOpen className="w-5 h-5 mr-3" />
                Course View
                <ChevronRight className="w-4 h-4 ml-auto" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start" className="w-64">
              <DropdownMenuLabel>View a Course as Admin</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="admin-view-bea"
                onClick={() => navigate('/dashboard')}
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Bail Enforcement Certification
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="admin-view-pl"
                onClick={() => navigate('/pre-licensing/dashboard')}
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Bail Bond Pre-Licensing
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" className="w-full justify-start text-muted-foreground hover:text-destructive" onClick={handleLogout}>
            <LogOut className="w-5 h-5 mr-3" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-black" style={{ fontFamily: 'Barlow Condensed' }}>
                {activeTab === 'students' && 'STUDENT MANAGEMENT'}
                {activeTab === 'certificates' && 'CERTIFICATE APPROVALS'}
                {activeTab === 'exams' && 'EXAM RESULTS'}
                {activeTab === 'discounts' && 'DISCOUNT CODES'}
                {activeTab === 'audit' && 'AUDIT LOGS'}
                {activeTab === 'proctor-emails' && 'PROCTOR EMAILS'}
                {activeTab === 'tools' && 'DATA TOOLS'}
              </h1>
              <p className="text-muted-foreground">
                {activeTab === 'students' && `${students.length} registered students`}
                {activeTab === 'certificates' && `${certificates.length} pending review`}
                {activeTab === 'exams' && `${examAttempts.length} exam attempts`}
                {activeTab === 'discounts' && `${discountCodes.length} active codes`}
                {activeTab === 'audit' && `${auditLogs.length} log entries`}
                {activeTab === 'proctor-emails' && `${proctorEmails.length} email logs (MOCKED)`}
                {activeTab === 'tools' && 'Database maintenance & cleanup utilities'}
              </p>
            </div>
            <Button onClick={loadAllData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Students Tab */}
          {activeTab === 'students' && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search students..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="bg-card border border-border rounded-sm">
                <div className="grid grid-cols-5 gap-4 p-4 border-b border-border text-sm font-semibold text-muted-foreground">
                  <div>Name</div>
                  <div>Email</div>
                  <div>Status</div>
                  <div>Registered</div>
                  <div>Actions</div>
                </div>
                <div className="divide-y divide-border">
                  {filteredStudents.map((student) => (
                    <div key={student.id} className="grid grid-cols-5 gap-4 p-4 items-center hover:bg-secondary/50 transition-colors">
                      <div className="font-medium">{student.first_name} {student.last_name}</div>
                      <div className="text-sm text-muted-foreground">{student.email}</div>
                      <div>
                        {student.is_suspended ? (
                          <span className="px-2 py-0.5 rounded-sm text-xs bg-red-500/10 text-red-500 font-bold">SUSPENDED</span>
                        ) : (
                          <span className={`px-2 py-0.5 rounded-sm text-xs ${
                            student.has_paid ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'
                          }`}>
                            {student.has_paid ? 'ENROLLED' : 'PENDING'}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">{formatDate(student.created_at)}</div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => loadStudentProgress(student.id)}>
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleResetPassword(student.id, student.email)}
                          data-testid={`reset-pwd-${student.id}`}
                        >
                          <Lock className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-500 hover:bg-red-500/10"
                          onClick={() => handleDeleteStudent(student.id, student.email)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {filteredStudents.length === 0 && (
                    <div className="p-8 text-center text-muted-foreground">
                      No students found
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Certificates Tab */}
          {activeTab === 'certificates' && (
            <div className="space-y-4">
              {certificates.length === 0 ? (
                <div className="bg-card border border-border rounded-sm p-8 text-center">
                  <Award className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold mb-2">No Pending Certificates</h3>
                  <p className="text-muted-foreground text-sm">
                    Certificates will appear here when students complete the final exam
                  </p>
                </div>
              ) : (
                <div className="bg-card border border-border rounded-sm">
                  {certificates.map((cert) => (
                    <div key={cert.id} className="p-6 border-b border-border last:border-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{cert.user_name}</h3>
                          <p className="text-sm text-muted-foreground">{cert.user_email}</p>
                          <div className="mt-2 text-sm">
                            <span className="text-muted-foreground">Course:</span>{' '}
                            <span className="font-semibold">
                              {cert.course === 'pre-licensing' ? 'Bail Bond Pre-Licensing' : 'Bail Enforcement Certification'}
                            </span>
                          </div>
                          <div className="text-sm">
                            <span className="text-muted-foreground">Exam Score:</span>{' '}
                            <span className="font-semibold">{cert.final_exam_score}%</span>
                          </div>
                          <div className="text-sm">
                            <span className="text-muted-foreground">Completed:</span>{' '}
                            <span>{formatDate(cert.course_completed)}</span>
                          </div>
                          <div className="text-sm">
                            <span className="text-muted-foreground">Certificate #:</span>{' '}
                            <span className="font-mono">{cert.certificate_number}</span>
                            <button
                              data-testid={`edit-cert-number-${cert.id}`}
                              className="ml-2 text-xs text-primary hover:underline"
                              onClick={async () => {
                                const newNum = prompt('Enter new certificate number:', cert.certificate_number);
                                if (newNum && newNum !== cert.certificate_number) {
                                  try {
                                    await axios.post(`${API}/admin/certificates/${cert.id}/set-number`, 
                                      { certificate_number: newNum }, { headers: getAuthHeaders() });
                                    toast.success(`Changed to ${newNum.toUpperCase()}`);
                                    loadAllData();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Failed to change number');
                                  }
                                }
                              }}
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleCertificateReview(cert.id, false)}>
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                          <Button size="sm" onClick={() => handleCertificateReview(cert.id, true)}>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Exam Results Tab */}
          {activeTab === 'exams' && (
            <div className="bg-card border border-border rounded-sm">
              <div className="grid grid-cols-6 gap-4 p-4 border-b border-border text-sm font-semibold text-muted-foreground">
                <div>Student</div>
                <div>Score</div>
                <div>Status</div>
                <div>Proctor</div>
                <div>Date</div>
                <div>Verified</div>
              </div>
              <div className="divide-y divide-border">
                {examAttempts.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No exam attempts yet
                  </div>
                ) : (
                  examAttempts.map((attempt) => (
                    <div key={attempt.id} className="grid grid-cols-6 gap-4 p-4 items-center">
                      <div className="text-sm">{attempt.user_id?.slice(0, 8)}...</div>
                      <div className="font-semibold">{attempt.percentage}%</div>
                      <div>
                        <span className={`px-2 py-0.5 rounded-sm text-xs ${
                          attempt.passed ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                        }`}>
                          {attempt.passed ? 'PASSED' : 'FAILED'}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">{attempt.proctor_name || 'N/A'}</div>
                      <div className="text-sm text-muted-foreground">{formatDate(attempt.submitted_at)}</div>
                      <div>
                        <span className={`px-2 py-0.5 rounded-sm text-xs ${
                          attempt.proctor_verified ? 'bg-green-500/10 text-green-500' : 'bg-yellow-500/10 text-yellow-500'
                        }`}>
                          {attempt.proctor_verified ? 'VERIFIED' : 'PENDING'}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Discount Codes Tab */}
          {activeTab === 'discounts' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={() => setShowDiscountForm(!showDiscountForm)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Code
                </Button>
              </div>

              {showDiscountForm && (
                <form onSubmit={handleCreateDiscountCode} className="bg-card border border-border rounded-sm p-6">
                  <h3 className="font-semibold mb-4">Create Discount Code(s)</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <Label>Code (leave blank to auto-generate)</Label>
                      <Input
                        value={newDiscountCode.code}
                        onChange={(e) => setNewDiscountCode(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                        placeholder="Auto-generate"
                        data-testid="discount-code-input"
                      />
                    </div>
                    <div>
                      <Label>Course</Label>
                      <select
                        value={newDiscountCode.course}
                        onChange={(e) => setNewDiscountCode(prev => ({ ...prev, course: e.target.value }))}
                        className="w-full h-10 px-3 rounded-sm border border-border bg-background text-sm"
                        data-testid="discount-course-select"
                      >
                        <option value="">Any Course</option>
                        <option value="bail-enforcement">Bail Enforcement</option>
                        <option value="pre-licensing">Bail Bond Pre-Licensing</option>
                      </select>
                    </div>
                    <div>
                      <Label>Discount %</Label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={newDiscountCode.discount_percent}
                        onChange={(e) => setNewDiscountCode(prev => ({ ...prev, discount_percent: e.target.value }))}
                        required
                        data-testid="discount-percent-input"
                      />
                    </div>
                    <div>
                      <Label>Uses per code</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newDiscountCode.max_uses}
                        onChange={(e) => setNewDiscountCode(prev => ({ ...prev, max_uses: e.target.value }))}
                        data-testid="discount-max-uses-input"
                      />
                    </div>
                    <div>
                      <Label>How many codes?</Label>
                      <Input
                        type="number"
                        min="1"
                        max="50"
                        value={newDiscountCode.quantity}
                        onChange={(e) => setNewDiscountCode(prev => ({ ...prev, quantity: e.target.value }))}
                        data-testid="discount-quantity-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Each code is single-use by default. Leave "Code" blank to auto-generate unique codes (e.g., BEA-A3X9K2).
                  </p>
                  <div className="mt-3">
                    <Label>Email code to (optional)</Label>
                    <Input
                      type="email"
                      value={newDiscountCode.send_to_email}
                      onChange={(e) => setNewDiscountCode(prev => ({ ...prev, send_to_email: e.target.value }))}
                      placeholder="student@example.com — leave blank to skip"
                      className="max-w-md"
                      data-testid="discount-email-input"
                    />
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button type="submit" data-testid="discount-create-btn">Generate</Button>
                    <Button type="button" variant="outline" onClick={() => setShowDiscountForm(false)}>Cancel</Button>
                  </div>
                </form>
              )}

              <div className="bg-card border border-border rounded-sm">
                <div className="grid grid-cols-5 gap-4 p-4 border-b border-border text-sm font-semibold text-muted-foreground">
                  <div>Code</div>
                  <div>Discount</div>
                  <div>Uses</div>
                  <div>Status</div>
                  <div>Actions</div>
                </div>
                <div className="divide-y divide-border">
                  {discountCodes.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      No discount codes created
                    </div>
                  ) : (
                    discountCodes.map((code) => (
                      <div key={code.id} className="grid grid-cols-5 gap-4 p-4 items-center">
                        <div>
                          <div className="font-mono font-semibold">{code.code}</div>
                          <div className="text-xs text-muted-foreground">
                            {code.course ? (code.course === 'pre-licensing' ? 'Pre-Licensing' : 'Bail Enforcement') : 'Any Course'}
                          </div>
                        </div>
                        <div>{code.discount_percent}%</div>
                        <div className="text-sm">
                          {code.times_used || 0} / {code.max_uses || '∞'}
                        </div>
                        <div>
                          <span className={`px-2 py-0.5 rounded-sm text-xs ${
                            (code.max_uses && (code.times_used || 0) >= code.max_uses)
                              ? 'bg-red-500/10 text-red-500'
                              : code.is_active ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                          }`}>
                            {(code.max_uses && (code.times_used || 0) >= code.max_uses) ? 'USED' : code.is_active ? 'ACTIVE' : 'INACTIVE'}
                          </span>
                        </div>
                        <div>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteDiscountCode(code.code)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Audit Logs Tab */}
          {activeTab === 'audit' && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Input
                  placeholder="Filter by action..."
                  value={auditFilter}
                  onChange={(e) => setAuditFilter(e.target.value)}
                  className="max-w-xs"
                />
              </div>

              <div className="bg-card border border-border rounded-sm">
                <div className="grid grid-cols-4 gap-4 p-4 border-b border-border text-sm font-semibold text-muted-foreground">
                  <div>Action</div>
                  <div>User</div>
                  <div>Details</div>
                  <div>Timestamp</div>
                </div>
                <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
                  {filteredLogs.slice(0, 100).map((log, i) => (
                    <div key={i} className="grid grid-cols-4 gap-4 p-4 items-center text-sm">
                      <div className="font-mono text-xs bg-secondary px-2 py-0.5 rounded w-fit">{log.action}</div>
                      <div className="text-muted-foreground">{log.user_id?.slice(0, 8)}...</div>
                      <div className="text-muted-foreground truncate">
                        {log.details ? JSON.stringify(log.details).slice(0, 50) : '-'}
                      </div>
                      <div className="text-muted-foreground">{log.timestamp_mt || formatDate(log.timestamp)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Proctor Emails Tab */}
          {activeTab === 'proctor-emails' && (
            <div className="space-y-4">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-4">
                <div className="flex items-center gap-2 text-yellow-600">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-semibold">MOCKED Email System</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Emails are logged but not actually sent. Connect SendGrid or Resend to enable delivery.
                </p>
              </div>

              <div className="bg-card border border-border rounded-sm">
                <div className="grid grid-cols-6 gap-4 p-4 border-b border-border text-sm font-semibold text-muted-foreground">
                  <div>Student</div>
                  <div>Proctor</div>
                  <div>Email Type</div>
                  <div>Score</div>
                  <div>Status</div>
                  <div>Sent At</div>
                </div>
                <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
                  {proctorEmails.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <h3 className="font-semibold mb-2">No Proctor Emails</h3>
                      <p className="text-sm">
                        Proctor verification emails will appear here when students complete proctored exams
                      </p>
                    </div>
                  ) : (
                    proctorEmails.map((email) => (
                      <div key={email.id} className="grid grid-cols-6 gap-4 p-4 items-center">
                        <div>
                          <div className="font-medium text-sm">{email.student_name}</div>
                          <div className="text-xs text-muted-foreground">{email.student_email}</div>
                        </div>
                        <div>
                          <div className="font-medium text-sm">{email.proctor_name}</div>
                          <div className="text-xs text-muted-foreground">{email.proctor_email}</div>
                        </div>
                        <div>
                          <span className="px-2 py-0.5 rounded-sm text-xs bg-secondary">
                            {email.email_type?.replace('_', ' ')}
                          </span>
                        </div>
                        <div className="font-semibold">{email.exam_score}%</div>
                        <div>
                          <span className={`px-2 py-0.5 rounded-sm text-xs ${
                            email.delivered 
                              ? 'bg-green-500/10 text-green-500' 
                              : 'bg-yellow-500/10 text-yellow-600'
                          }`}>
                            {email.delivered ? 'DELIVERED' : 'LOGGED ONLY'}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {formatDate(email.sent_at)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Data Tools Tab */}
          {activeTab === 'tools' && (
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-sm p-6">
                <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-primary" />
                  Cleanup Duplicate Progress Records
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Scans the database for duplicate chapter progress entries (same student + same chapter with multiple records). 
                  Merges them by summing time spent, keeping the best status, and removing extras. 
                  Also ensures the unique index is in place to prevent future duplicates.
                </p>
                <Button
                  data-testid="cleanup-duplicates-btn"
                  onClick={async () => {
                    if (!window.confirm('Run duplicate progress cleanup? This will merge any duplicate records and sum their time values.')) return;
                    try {
                      const res = await axios.post(`${API}/admin/cleanup-duplicates`, {}, { headers: getAuthHeaders() });
                      toast.success(res.data.message || 'Cleanup complete');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Cleanup failed');
                    }
                  }}
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/10"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Run Cleanup Now
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminPortalPage;
